"use client"

import { useLayoutEffect, useRef } from "react"
import { getCloseAttemptHandler, setCloseAttemptHandler, useGlobalDialogStore } from "@/stores/use-global-dialog-store"

import { AppDialogSurface, type AppDialogProps } from "./app-dialog"
import { useDialogEntryId } from "./dialog-entry-context"

type ContentProps<T> = T extends AppDialogProps ? Omit<T, "open" | "onOpenChange"> : never
export type AppDialogContentProps = ContentProps<Exclude<AppDialogProps, { variant: "alert" | "destroy" }>>

/** Content for imperative global dialogs. State and actions remain in the feature component. */
export function AppDialogContent(props: AppDialogContentProps) {
  const entryId = useDialogEntryId()
  const currentProps = useRef(props)
  currentProps.current = props
  useLayoutEffect(() => {
    if (!entryId) return
    const guard = () => {
      const current = currentProps.current
      if (current.dismiss === "none") return
      if (current.onClose) current.onClose()
      else useGlobalDialogStore.getState().popDialog()
    }
    setCloseAttemptHandler(guard, entryId)
    return () => setCloseAttemptHandler(null, entryId)
  }, [entryId])
  const setVariant = useGlobalDialogStore((state) => state.setEntryVariant)
  const closeDialog = useGlobalDialogStore((state) => state.popDialog)
  useLayoutEffect(() => {
    if (entryId) setVariant(entryId, props.variant, props.dismiss ?? "all")
  }, [props.variant, props.dismiss, entryId, setVariant])
  return (
    <AppDialogSurface
      {...props}
      open
      onClose={() => (getCloseAttemptHandler(entryId) ?? closeDialog)()}
      onOpenChange={(open) => {
        if (!open) closeDialog()
      }}
    />
  )
}
